import { useRef, useEffect } from "react";
import { throttle } from "lodash";

/**
 * Hook to handle sticky fade effect on scroll.
 * Returns a ref to be attached to the sticky element.
 */
export function useFadeSticky(
  { calculateTopFromTabs } = { calculateTopFromTabs: false }
) {
  const stickyRef = useRef(null);
  const originalTop = useRef(null);

  useEffect(() => {
    let lastScrollY = window.scrollY;
    if (stickyRef.current) {
      originalTop.current =
        stickyRef.current.getBoundingClientRect().top + window.scrollY;
      stickyRef.current.style.transition =
        "opacity 0.3s ease-in-out, top 0.1s linear";
    }

    const handleScroll = throttle(() => {
      if (!stickyRef.current) return;
      const scrollY = window.scrollY;
      const scrollDiff = scrollY - lastScrollY;

      const isOutOfViewport =
        window.scrollY - originalTop.current > originalTop.current;
      if (scrollDiff < -30) {
        stickyRef.current.style.opacity = "1";
        stickyRef.current.style.pointerEvents = "auto";
      } else if (scrollDiff > 30 && isOutOfViewport) {
        stickyRef.current.style.opacity = "0";
        stickyRef.current.style.pointerEvents = "none";
      }

      lastScrollY = scrollY;
    }, 100);

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!calculateTopFromTabs || !stickyRef.current) return;
    const calculateTop = throttle(() => {
      const tabs =
        document.getElementById("timeline-tabs") ??
        document.getElementById("home-tabs");
      if (!tabs) return;
      const tabsTop = Number(
        tabs.style.top.slice(0, tabs.style.top.indexOf("px"))
      );
      if (tabsTop) {
        stickyRef.current.style.top = `${tabsTop + tabs.offsetHeight}px`;
      }
    }, 100);
    calculateTop();
    window.addEventListener("scroll", calculateTop);
    return () => window.removeEventListener("scroll", calculateTop);
  }, [calculateTopFromTabs, stickyRef]);

  return stickyRef;
}

export default useFadeSticky;
